/**
 * Welcome message hook for managing welcome state
 * Follows ADR-003: High Cohesion - Single responsibility: welcome message logic
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Options for useWelcomeMessage hook
 */
export interface UseWelcomeMessageOptions {
  /** Session ID */
  sessionId: string | null;
  /** Number of messages in the chat */
  messageCount: number;
  /** Whether session is being created */
  isCreatingSession: boolean;
  /** Whether parent component has shown welcome */
  parentHasShownWelcome?: boolean;
  /** Callback when welcome is shown */
  onWelcomeShown?: () => void;
  /** Scroll to bottom function */
  scrollToBottom?: (force?: boolean) => void;
}

/**
 * Return value for useWelcomeMessage hook
 */
export interface UseWelcomeMessageReturn {
  /** Whether to show the welcome message */
  showWelcomeMessage: boolean;
  /** Callback when welcome message appears */
  handleWelcomeMessageAppear: () => void;
  /** Reset welcome state for new session */
  resetWelcomeState: () => void;
}

/**
 * Custom hook for managing welcome message display state
 */
export function useWelcomeMessage({
  sessionId,
  messageCount,
  isCreatingSession,
  parentHasShownWelcome,
  onWelcomeShown,
  scrollToBottom
}: UseWelcomeMessageOptions): UseWelcomeMessageReturn {
  const [hasSimulatedWelcome, setHasSimulatedWelcome] = useState<boolean>(
    parentHasShownWelcome || false
  );
  const [showWelcomeMessage, setShowWelcomeMessage] = useState<boolean>(false);

  /**
   * Effect to show welcome message when chat starts
   */
  useEffect(() => {
    if (
      sessionId &&
      messageCount === 0 &&
      !hasSimulatedWelcome &&
      !isCreatingSession &&
      !parentHasShownWelcome
    ) {
      setShowWelcomeMessage(true);
      setHasSimulatedWelcome(true);
    } else if (
      sessionId &&
      messageCount === 0 &&
      !showWelcomeMessage &&
      parentHasShownWelcome &&
      !isCreatingSession
    ) {
      setShowWelcomeMessage(true);
    }
  }, [sessionId, messageCount, hasSimulatedWelcome, isCreatingSession, parentHasShownWelcome, showWelcomeMessage]);

  /**
   * Callback when welcome message appears
   */
  const handleWelcomeMessageAppear = useCallback((): void => {
    onWelcomeShown?.();
    setTimeout(() => {
      scrollToBottom?.(true);
    }, 100);
  }, [scrollToBottom, onWelcomeShown]);

  /**
   * Reset welcome state for new session
   */
  const resetWelcomeState = useCallback((): void => {
    setHasSimulatedWelcome(false);
    setShowWelcomeMessage(false);
  }, []);

  return {
    showWelcomeMessage,
    handleWelcomeMessageAppear,
    resetWelcomeState
  };
}

